import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  FileText, Plus, X, Save, Loader2, Trash2, Download, SendHorizonal,
  User, Mail, Phone, Ship, Calendar, Eye, CheckCircle2, Edit, Copy, ExternalLink,
  ImagePlus, CreditCard, Upload, MapPin, Anchor, RefreshCcw
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  draft: { style: 'bg-slate-100 text-slate-700 border border-slate-300', label: 'Brouillon' },
  sent: { style: 'bg-blue-100 text-blue-800 border border-blue-300', label: 'Envoye' },
  viewed: { style: 'bg-yellow-100 text-yellow-800 border border-yellow-300', label: 'Consulte' },
  signed: { style: 'bg-emerald-100 text-emerald-800 border border-emerald-300', label: 'Signe' },
};

const DEFAULT_INCLUSIONS = [
  "La croisiere pour les passagers indiques",
  "Le logement en cabine double",
  "Les 3 repas par jour (pas de repas le midi les jours d'arrivee et depart)",
  "Les boissons a volonte toute la journee (hors champagnes)",
  "Les services du Capitaine et de l'hotesse",
  "Les assurances RC Armateur",
  "Le transport ALLER / RETOUR de l'aeroport jusqu'aux ports (Uniquement Ajaccio)",
  "Les sports nautiques a bord : paddle, snorkeling, peche..."
];

const DEFAULT_EXCLUSIONS = [
  "Les taxes eventuelles de sejour et de sortie du territoire",
  "Le petit dejeuner du jour 1 ainsi que le diner du jour du debarquement",
  "Les repas et les boissons non inclus dans la formule (ex : champagne)",
  "La caisse de bord : 240 EUR / passager / semaine",
  "Les depenses d'ordre personnel",
  "Les excursions facultatives et les activites non mentionnees au programme",
  "Les repas eventuels aux escales",
  "Les garanties assistance, rapatriement, frais medicaux et d'hospitalisation, assistance juridique et penale",
  "Les garanties annulation, bagages, retard aerien"
];

const DEFAULT_CAUTION = `UNE CAUTION SERA DEMANDEE LORS DE L'EMBARQUEMENT
La caution n'est prelevee qu'en cas de dommages causes a l'interieur des cabines et/ou dans les parties communes. Si aucun dommage lors de la semaine de croisiere, elle est restituee le jour du debarquement.
CAUTION : 500 EUR / PASSAGER`;

// ─── PHOTO UPLOADER ───
const PhotoUploader = ({ photos, onChange, maxPhotos, label, token }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remaining = maxPhotos - photos.length;
    if (remaining <= 0) { toast.error(`Maximum ${maxPhotos} photos`); return; }

    setUploading(true);
    const newUrls = [];
    for (const file of files.slice(0, remaining)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload`, fd, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000,
        });
        newUrls.push(res.data.url);
      } catch (err) { toast.error(`Erreur upload ${file.name}: ${err.response?.data?.detail || err.message || 'Erreur inconnue'}`); }
    }
    if (newUrls.length) onChange([...photos, ...newUrls]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const remove = (idx) => onChange(photos.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold flex items-center gap-1.5">
        <ImagePlus className="h-3.5 w-3.5" /> {label} ({photos.length}/{maxPhotos})
      </Label>
      <div className="flex flex-wrap gap-2">
        {photos.map((url, i) => (
          <div key={i} className="relative group w-20 h-20 rounded-lg overflow-hidden border-2 border-slate-200">
            <img
              src={url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url}
              alt=""
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {photos.length < maxPhotos && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-[#D4AF37] flex flex-col items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors"
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-5 w-5" /><span className="text-[10px] mt-0.5">Ajouter</span></>}
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
    </div>
  );
};

// ─── LIST EDITOR ───
const ListEditor = ({ items, onChange, label, placeholder }) => {
  const addItem = () => onChange([...items, '']);
  const updateItem = (idx, val) => { const n = [...items]; n[idx] = val; onChange(n); };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold">{label}</Label>
      {items.map((item, i) => (
        <div key={i} className="flex gap-1.5">
          <Input value={item} onChange={e => updateItem(i, e.target.value)} className="h-8 text-sm" placeholder={placeholder} />
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 shrink-0" onClick={() => removeItem(i)}><X className="h-3.5 w-3.5" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addItem}><Plus className="h-3 w-3 mr-1" /> Ajouter</Button>
    </div>
  );
};

const fmtDate = (d) => {
  if (!d) return '-';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

// ─── DEVIS BUILDER FORM ───
const DevisBuilder = ({ devisData, cruises, token, onSaved, onCancel }) => {
  const isEdit = !!devisData?.id;

  const [form, setForm] = useState({
    type: 'cabin',
    devis_number: '',
    client_name: '', client_email: '', client_phone: '', client_address: '',
    cruise_id: '', cruise_name: '', program: '',
    start_date: '', end_date: '',
    departure_time: '15H30', arrival_time: '17H00', disembark_time: '08H00',
    departure_port: 'Ajaccio', catamaran: 'LAGOON 46/38', duration: '8 jours / 7 nuits',
    passenger_count: 2,
    price_per_person: 0, privatization_price: 0,
    caisse_de_bord_per_person: 240, caution_per_person: 500,
    items: [],
    inclusions: DEFAULT_INCLUSIONS,
    exclusions: DEFAULT_EXCLUSIONS,
    caution_text: DEFAULT_CAUTION,
    boat_photos: [],
    boat_plans: [],
    intro_text: '',
    notes: '', validity_days: 8,
    quote_request_id: null,
    ...devisData,
  });

  // Auto-fill defaults for inclusions/exclusions if empty on edit
  useEffect(() => {
    if (isEdit) {
      setForm(f => ({
        ...f,
        inclusions: f.inclusions?.length ? f.inclusions : DEFAULT_INCLUSIONS,
        exclusions: f.exclusions?.length ? f.exclusions : DEFAULT_EXCLUSIONS,
        caution_text: f.caution_text || DEFAULT_CAUTION,
      }));
    }
  }, [isEdit]);

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [useCustomItems, setUseCustomItems] = useState(devisData?.items?.length > 0);

  const isPrivat = form.type === 'privatization';
  const pax = parseInt(form.passenger_count) || 2;
  const basePrice = isPrivat ? (parseFloat(form.privatization_price) || 0) : (parseFloat(form.price_per_person) || 0) * pax;
  const totalCdb = (parseFloat(form.caisse_de_bord_per_person) || 0) * pax;

  let total;
  if (useCustomItems && form.items.length > 0) {
    total = form.items.reduce((s, it) => s + (parseFloat(it.total) || 0), 0);
  } else {
    total = basePrice + totalCdb;
  }

  const handleCruiseChange = (cruiseId) => {
    const c = cruises.find(cr => cr.id === cruiseId);
    if (c) {
      setForm(f => ({
        ...f, cruise_id: c.id, cruise_name: c.name, program: c.name,
        departure_port: c.departure_port || 'Ajaccio',
        duration: c.duration || '8 jours / 7 nuits',
        price_per_person: c.base_price || 0,
        privatization_price: c.privatization_base_price || 0,
      }));
    }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, unit_price: 0, total: 0 }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        items[idx].total = (parseFloat(items[idx].quantity) || 0) * (parseFloat(items[idx].unit_price) || 0);
      }
      return { ...f, items };
    });
  };

  const handleSubmit = async () => {
    if (!form.client_name || !form.type) {
      toast.error('Remplissez au minimum le nom du client et le type');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, passenger_count: pax, items: useCustomItems ? form.items : [] };
      if (!isEdit && form.devis_number?.trim()) {
        payload.devis_number = form.devis_number.trim();
      } else if (!isEdit) {
        delete payload.devis_number;
      }
      let res;
      if (isEdit) {
        res = await axios.put(`${API}/devis/${devisData.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        res = await axios.post(`${API}/devis`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(res.data.message);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: User },
    { id: 'photos', label: 'Photos', icon: ImagePlus },
    { id: 'prestation', label: 'Prestation', icon: Ship },
    { id: 'tarif', label: 'Tarification', icon: CreditCard },
  ];

  return (
    <Card className="border-2 border-[#D4AF37] shadow-lg" data-testid="devis-builder">
      <CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg text-[#0A2540] flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#D4AF37]" />
            {isEdit ? `Modifier ${devisData.devis_number}` : 'Nouveau devis'}
          </h3>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onCancel}><X className="h-4 w-4" /></Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-xs font-medium transition-all ${
                activeTab === t.id ? 'bg-white text-[#0A2540] shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              <t.icon className="h-3.5 w-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* Tab: General */}
        {activeTab === 'general' && (
          <div className="space-y-4">
            {/* Type */}
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setForm(f => ({ ...f, type: 'cabin' }))}
                className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${!isPrivat ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#0A2540]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                data-testid="devis-type-cabin">
                Reservation Cabine
              </button>
              <button type="button" onClick={() => setForm(f => ({ ...f, type: 'privatization' }))}
                className={`p-3 rounded-lg border-2 text-sm font-semibold transition-all ${isPrivat ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#0A2540]' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                data-testid="devis-type-privat">
                Privatisation
              </button>
            </div>

            {/* Custom Devis Number */}
            {!isEdit && (
              <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg p-3">
                <Label className="text-xs font-semibold text-[#0A2540] flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" /> N° de devis (optionnel)
                </Label>
                <Input
                  value={form.devis_number}
                  onChange={e => setForm(f => ({ ...f, devis_number: e.target.value }))}
                  className="h-9 mt-1.5"
                  placeholder="Ex: SDMA 0257 (laisser vide pour auto-increment)"
                  data-testid="devis-custom-number"
                />
                <p className="text-[10px] text-slate-400 mt-1">Si vide, un numero sera attribue automatiquement</p>
              </div>
            )}

            {/* Client */}
            <div className="bg-[#0A2540]/5 border border-[#0A2540]/10 rounded-lg p-3 space-y-3">
              <h4 className="text-xs font-bold text-[#0A2540] uppercase tracking-wider flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> Client</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold">Nom complet *</Label><Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} className="h-9" data-testid="devis-client-name" /></div>
                <div><Label className="text-xs">Email *</Label><Input value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} className="h-9" data-testid="devis-client-email" /></div>
                <div><Label className="text-xs">Telephone</Label><Input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} className="h-9" /></div>
                <div><Label className="text-xs">Adresse</Label><Input value={form.client_address} onChange={e => setForm(f => ({ ...f, client_address: e.target.value }))} className="h-9" /></div>
              </div>
            </div>

            {/* Cruise */}
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 space-y-3">
              <h4 className="text-xs font-bold text-[#0A2540] uppercase tracking-wider flex items-center gap-1.5"><Anchor className="h-3.5 w-3.5" /> Croisiere</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold">Destination</Label>
                  <Select value={form.cruise_id} onValueChange={handleCruiseChange}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Choisir une croisiere" /></SelectTrigger>
                    <SelectContent>{cruises.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Programme</Label><Input value={form.program} onChange={e => setForm(f => ({ ...f, program: e.target.value }))} className="h-9" /></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Date debut</Label><Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="h-9" /></div>
                <div><Label className="text-xs">Date fin</Label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="h-9" /></div>
                <div><Label className="text-xs">Heure embarq.</Label><Input value={form.departure_time} onChange={e => setForm(f => ({ ...f, departure_time: e.target.value }))} className="h-9" /></div>
                <div><Label className="text-xs">Heure retour</Label><Input value={form.arrival_time} onChange={e => setForm(f => ({ ...f, arrival_time: e.target.value }))} className="h-9" /></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Heure debarq.</Label><Input value={form.disembark_time} onChange={e => setForm(f => ({ ...f, disembark_time: e.target.value }))} className="h-9" /></div>
                <div><Label className="text-xs">Port</Label><Input value={form.departure_port} onChange={e => setForm(f => ({ ...f, departure_port: e.target.value }))} className="h-9" /></div>
                <div><Label className="text-xs">Catamaran</Label><Input value={form.catamaran} onChange={e => setForm(f => ({ ...f, catamaran: e.target.value }))} className="h-9" /></div>
                <div><Label className="text-xs">Duree</Label><Input value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} className="h-9" /></div>
              </div>
              <div><Label className="text-xs font-semibold">Nombre de passagers</Label><Input type="number" min="1" max="8" value={form.passenger_count} onChange={e => setForm(f => ({ ...f, passenger_count: parseInt(e.target.value) || 2 }))} className="h-9 w-24" /></div>
            </div>
          </div>
        )}

        {/* Tab: Photos */}
        {activeTab === 'photos' && (
          <div className="space-y-4">
            <PhotoUploader photos={form.boat_photos} onChange={v => setForm(f => ({ ...f, boat_photos: v }))} maxPhotos={6} label="Photos du bateau (max 6)" token={token} />
            <PhotoUploader photos={form.boat_plans} onChange={v => setForm(f => ({ ...f, boat_plans: v }))} maxPhotos={2} label="Plan du bateau (max 2)" token={token} />
          </div>
        )}

        {/* Tab: Prestation */}
        {activeTab === 'prestation' && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <ListEditor items={form.inclusions} onChange={v => setForm(f => ({ ...f, inclusions: v }))} label="CE TARIF COMPREND" placeholder="Ex: La croisiere de 8 jours pour 2 passagers" />
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <ListEditor items={form.exclusions} onChange={v => setForm(f => ({ ...f, exclusions: v }))} label="LE TARIF NE COMPREND PAS" placeholder="Ex: Les depenses d'ordre personnel" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
              <Label className="text-xs font-bold text-amber-800 uppercase">Caution</Label>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Montant caution / passager (EUR)</Label><Input type="number" value={form.caution_per_person} onChange={e => setForm(f => ({ ...f, caution_per_person: parseFloat(e.target.value) || 0 }))} className="h-9 border-amber-300" /></div>
              </div>
              <Textarea value={form.caution_text} onChange={e => setForm(f => ({ ...f, caution_text: e.target.value }))} className="min-h-[80px] text-sm border-amber-300" placeholder="Texte de la caution..." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Texte d'introduction personnalise */}
              <div className="md:col-span-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <Label className="text-xs font-semibold text-[#0A2540] flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-amber-600" /> Texte d'introduction (apparait dans le PDF, l'email et la page de signature)
                </Label>
                <Textarea
                  value={form.intro_text}
                  onChange={e => setForm(f => ({ ...f, intro_text: e.target.value }))}
                  rows={3}
                  className="text-sm mt-1.5"
                  placeholder="Ex: Suite a notre echange, j'ai le plaisir de vous proposer ce devis pour votre croisiere en catamaran..."
                  data-testid="devis-intro-text"
                />
              </div>

              <div className="md:col-span-3"><Label className="text-xs">Notes internes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="min-h-[50px] text-sm" /></div>
              <div><Label className="text-xs">Validite (jours)</Label><Input type="number" value={form.validity_days} onChange={e => setForm(f => ({ ...f, validity_days: parseInt(e.target.value) || 8 }))} className="h-9" /></div>
            </div>
          </div>
        )}

        {/* Tab: Tarification */}
        {activeTab === 'tarif' && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-3">
              <h4 className="text-xs font-bold text-emerald-800 uppercase">Prix</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {isPrivat ? (
                  <div className="col-span-2"><Label className="text-xs text-emerald-700 font-medium">Prix privatisation (EUR)</Label><Input type="number" value={form.privatization_price} onChange={e => setForm(f => ({ ...f, privatization_price: parseFloat(e.target.value) || 0 }))} className="h-9 border-emerald-300" /></div>
                ) : (
                  <div className="col-span-2"><Label className="text-xs text-emerald-700 font-medium">Prix par passager (EUR)</Label><Input type="number" value={form.price_per_person} onChange={e => setForm(f => ({ ...f, price_per_person: parseFloat(e.target.value) || 0 }))} className="h-9 border-emerald-300" /></div>
                )}
                <div><Label className="text-xs text-emerald-700 font-medium">Caisse de bord / pax</Label><Input type="number" value={form.caisse_de_bord_per_person} onChange={e => setForm(f => ({ ...f, caisse_de_bord_per_person: parseFloat(e.target.value) || 0 }))} className="h-9 border-emerald-300" /></div>
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" checked={useCustomItems} onChange={e => setUseCustomItems(e.target.checked)} className="rounded" id="custom-items" />
                <label htmlFor="custom-items" className="text-xs text-emerald-700 font-medium cursor-pointer">Lignes de devis personnalisees</label>
              </div>

              {useCustomItems && (
                <div className="space-y-2">
                  {form.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white rounded p-2 border border-emerald-200">
                      <Input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="h-8 text-sm flex-1" placeholder="Description" />
                      <Input type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="h-8 text-sm w-16" placeholder="Qty" />
                      <Input type="number" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="h-8 text-sm w-24" placeholder="P.U." />
                      <span className="text-sm font-bold text-emerald-800 w-24 text-right">{(parseFloat(item.total) || 0).toLocaleString('fr-FR')} EUR</span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeItem(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addItem}><Plus className="h-3 w-3 mr-1" /> Ajouter une ligne</Button>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-emerald-200">
                {!useCustomItems && <div className="text-sm text-emerald-700">Base : {basePrice.toLocaleString('fr-FR')} EUR + Caisse de bord : {totalCdb.toLocaleString('fr-FR')} EUR</div>}
                <div className="text-right ml-auto">
                  <span className="text-xs text-emerald-600 uppercase">Total TTC</span>
                  <div className="text-2xl font-bold text-[#0A2540]">{total.toLocaleString('fr-FR')} EUR</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save */}
        <div className="flex gap-2 pt-2 border-t">
          <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white" onClick={handleSubmit} disabled={saving} data-testid="devis-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {saving ? 'Sauvegarde...' : isEdit ? 'Mettre a jour' : 'Creer le devis'}
          </Button>
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── MAIN COMPONENT ───
const AdminDevis = () => {
  const [devisList, setDevisList] = useState([]);
  const [cruises, setCruises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showBuilder, setShowBuilder] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [payLinkLoading, setPayLinkLoading] = useState(null);
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const fromQuote = searchParams.get('from_quote');
    if (fromQuote) {
      setEditData({
        quote_request_id: fromQuote,
        client_name: searchParams.get('client_name') || '',
        client_email: searchParams.get('client_email') || '',
        client_phone: searchParams.get('client_phone') || '',
        cruise_name: searchParams.get('cruise_name') || '',
        program: searchParams.get('cruise_name') || '',
        type: searchParams.get('type') || 'cabin',
        passenger_count: parseInt(searchParams.get('passenger_count')) || 2,
      });
      setShowBuilder(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const fetchAll = useCallback(async () => {
    try {
      const [dRes, cRes] = await Promise.all([
        axios.get(`${API}/devis`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cruises`),
      ]);
      setDevisList(dRes.data);
      setCruises(cRes.data);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/devis/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Devis supprime');
      setConfirmDelete(null);
      fetchAll();
    } catch { toast.error('Erreur'); }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await axios.post(`${API}/devis/${id}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchAll();
      setEditData(res.data.devis);
      setShowBuilder(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur duplication'); }
  };

  const downloadPdf = async (id, clientName, devisNumber) => {
    try {
      const res = await axios.get(`${API}/devis/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Devis_${devisNumber.replace(/ /g, '_')}_${clientName.replace(/ /g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF telecharge');
    } catch { toast.error('Erreur PDF'); }
  };

  const regeneratePdf = async (id, clientName, devisNumber) => {
    const ok = window.confirm(
      "Régénérer le PDF avec les CGV à jour et les nouvelles photos du bateau ?\n\n" +
      "✓ Le PDF sera téléchargé pour vous\n" +
      "✓ Une entrée sera ajoutée à l'historique\n" +
      "✗ AUCUN email ne sera envoyé au client"
    );
    if (!ok) return;
    try {
      const res = await axios.post(`${API}/devis/${id}/regenerate-pdf`, {}, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Devis_${devisNumber.replace(/ /g, '_')}_${clientName.replace(/ /g, '_')}_REGENERE.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF régénéré (CGV + photos à jour) — historique mis à jour');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur regénération PDF'); }
  };

  const sendByEmail = async (id) => {
    setSendingId(id);
    try {
      const res = await axios.post(`${API}/devis/${id}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur envoi'); }
    finally { setSendingId(null); }
  };

  const generatePaymentLink = async (id) => {
    setPayLinkLoading(id);
    try {
      const res = await axios.post(`${API}/devis/${id}/payment-link`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lien paiement'); }
    finally { setPayLinkLoading(null); }
  };

  const openEdit = async (id) => {
    try {
      const res = await axios.get(`${API}/devis/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setEditData(res.data);
      setShowBuilder(true);
    } catch { toast.error('Erreur'); }
  };

  const copySignLink = (signToken) => {
    navigator.clipboard.writeText(`${window.location.origin}/devis/sign/${signToken}`);
    toast.success('Lien copie');
  };

  const filtered = devisList.filter(d => filter === 'all' || d.status === filter);
  const stats = {
    total: devisList.length,
    draft: devisList.filter(d => d.status === 'draft').length,
    sent: devisList.filter(d => d.status === 'sent').length,
    signed: devisList.filter(d => d.status === 'signed').length,
    totalAmount: devisList.filter(d => d.status === 'signed').reduce((s, d) => s + (d.total || 0), 0),
  };

  return (
    <AdminLayout title="Devis">
      <div className="space-y-6" data-testid="admin-devis">
        {/* Stats */}
        <div className="flex flex-wrap gap-3">
          <div className="px-3 py-1.5 rounded-full bg-slate-100 text-sm font-medium text-slate-700">Total : {stats.total}</div>
          <div className="px-3 py-1.5 rounded-full bg-slate-100 text-sm font-medium text-slate-500">Brouillons : {stats.draft}</div>
          <div className="px-3 py-1.5 rounded-full bg-blue-100 text-sm font-medium text-blue-700">Envoyes : {stats.sent}</div>
          <div className="px-3 py-1.5 rounded-full bg-emerald-100 text-sm font-medium text-emerald-700">Signes : {stats.signed}</div>
          <div className="px-3 py-1.5 rounded-full bg-[#D4AF37]/20 text-sm font-bold text-[#D4AF37]">CA signe : {stats.totalAmount.toLocaleString('fr-FR')} EUR</div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white" onClick={() => { setEditData(null); setShowBuilder(true); }} data-testid="new-devis-btn">
            <Plus className="h-4 w-4 mr-2" /> Nouveau devis
          </Button>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Filtrer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="draft">Brouillons</SelectItem>
              <SelectItem value="sent">Envoyes</SelectItem>
              <SelectItem value="viewed">Consultes</SelectItem>
              <SelectItem value="signed">Signes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Builder */}
        {showBuilder && (
          <DevisBuilder
            devisData={editData}
            cruises={cruises}
            token={token}
            onSaved={() => { setShowBuilder(false); setEditData(null); fetchAll(); }}
            onCancel={() => { setShowBuilder(false); setEditData(null); }}
          />
        )}

        {/* List */}
        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 rounded-lg animate-pulse" />)}</div>
        ) : filtered.length > 0 ? (
          <div className="space-y-4">
            {filtered.map(d => {
              const st = statusConfig[d.status] || statusConfig.draft;
              const hasPhotos = (d.boat_photos?.length || 0) + (d.boat_plans?.length || 0) > 0;
              return (
                <Card key={d.id} className={`transition-all ${d.status === 'signed' ? 'border-l-4 border-l-emerald-500' : d.status === 'sent' ? 'border-l-4 border-l-blue-400' : ''}`} data-testid={`devis-card-${d.id}`}>
                  <CardContent className="p-3 sm:p-5">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                      <div className="space-y-2 flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${d.status === 'signed' ? 'bg-emerald-600' : 'bg-[#D4AF37]'}`}>
                            <FileText className="h-5 w-5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-[#0A2540] truncate">{d.devis_number} — {d.client_name}</h3>
                            <p className="text-xs text-[#4A5568]">
                              {d.type === 'privatization' ? 'Privatisation' : 'Cabine'}
                              {d.cruise_name && ` | ${d.cruise_name}`}
                              {d.passenger_count > 0 && ` | ${d.passenger_count} pax`}
                              {hasPhotos && ' | Photos'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-[#4A5568]"><Mail className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{d.client_email || '-'}</span></div>
                          <div className="flex items-center gap-2 text-[#4A5568]"><Calendar className="h-3.5 w-3.5 shrink-0" />{fmtDate(d.start_date)} → {fmtDate(d.end_date)}</div>
                          {d.signed_at && <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5 shrink-0" />Signe le {new Date(d.signed_at).toLocaleDateString('fr-FR')}</div>}
                          {d.square_payment_link && <div className="flex items-center gap-2 text-teal-600"><CreditCard className="h-3.5 w-3.5 shrink-0" />Lien paiement actif</div>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <Badge className={st.style}>{st.label}</Badge>
                        <p className="text-xl font-bold text-[#0A2540]">{(d.total || 0).toLocaleString('fr-FR')} EUR</p>
                        <div className="flex gap-1.5 flex-wrap justify-end">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEdit(d.id)}><Edit className="h-3.5 w-3.5 mr-1" />Modifier</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => handleDuplicate(d.id)} data-testid={`devis-duplicate-${d.id}`}><Copy className="h-3.5 w-3.5 mr-1" />Dupliquer</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadPdf(d.id, d.client_name, d.devis_number)}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-50" onClick={() => regeneratePdf(d.id, d.client_name, d.devis_number)} data-testid={`devis-regenerate-${d.id}`} title="Régénère le PDF avec CGV + photos à jour (sans email au client)"><RefreshCcw className="h-3.5 w-3.5 mr-1" />Régénérer PDF</Button>
                          <Button size="sm" className="bg-[#C4724F] hover:bg-[#C4724F]/90 text-white h-7 text-xs" onClick={() => sendByEmail(d.id)} disabled={sendingId === d.id}>
                            {sendingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <SendHorizonal className="h-3.5 w-3.5 mr-1" />}Envoyer
                          </Button>
                        </div>
                        <div className="flex gap-1.5 flex-wrap justify-end">
                          {!d.square_payment_link && (
                            <Button size="sm" variant="outline" className="h-7 text-xs border-teal-300 text-teal-700 hover:bg-teal-50" onClick={() => generatePaymentLink(d.id)} disabled={payLinkLoading === d.id}>
                              {payLinkLoading === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CreditCard className="h-3.5 w-3.5 mr-1" />}Lien paiement
                            </Button>
                          )}
                          {d.square_payment_link && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { navigator.clipboard.writeText(d.square_payment_link); toast.success('Lien paiement copie'); }}>
                              <Copy className="h-3.5 w-3.5 mr-1" />Copier lien paiement
                            </Button>
                          )}
                          {d.sign_token && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copySignLink(d.sign_token)}><Copy className="h-3.5 w-3.5 mr-1" />Lien signature</Button>
                          )}
                          {d.sign_token && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
                              <a href={`/devis/sign/${d.sign_token}`} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" />Apercu</a>
                            </Button>
                          )}
                          {confirmDelete === d.id ? (
                            <div className="flex items-center gap-1">
                              <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(d.id)}>Supprimer</Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setConfirmDelete(null)}><X className="h-3.5 w-3.5" /></Button>
                            </div>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => setConfirmDelete(d.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card><CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
            <p className="text-[#4A5568]">Aucun devis pour le moment</p>
          </CardContent></Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDevis;
